import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import PropTypes from 'prop-types';
import { getFilters } from '../../../webcommon/header/filterheader/selectors';
import FilterHeader from '../../../webcommon/header/filterheader/FilterHeader';
import { noop } from 'lodash';
import Grid from '../../../webcommon/grid/Grid';
import Modal from '../../../webcommon/common/modal/Modal';
import { ReactComponent as Rerun } from '../../../webcommon/common/Icons/Rerun.svg';
import { ReactComponent as AddCircle } from '../../../webcommon/common/Icons/Add-Circle.svg';
import moment from 'moment';
import defaultStyles from './WebJobs.module.scss';
import {
    webJobsLoaded, loadRequestOpened, createLoadRequest, setRowChecked,
    setAllRowsChecked, setDetailsBeingViewed, rerunSelectedRows, refreshData,
    setGridSorts, refreshWebJobsDataLoads
} from './actions';
import GridHeader from '../../../common/gridheader/GridHeader';
import ExportAsCsvButton from '../../../common/gridheader/exportbutton/ExportAsCsvButton';
import {
    getLoading, getRows, combinedColumnsSelector, getLoadRequestMappings, getDateFrom, getDateTo,
    getLastUpdated,
    getDefaultSort
} from './selectors';
import CreateLoadRequest from './createloadrequest/CreateLoadRequest';
import gridHeaderStyles from './GridHeaderStyles.module.scss';
import SimpleGridHeaderButton from '../../../common/gridheader/simplegridheader/SimpleGridHeaderButton';
import { findGridSort } from '../../../common/helpermethods/HelperMethods';
import { PAGE_REFRESH_RATE } from './constants';
import * as appConstants from '../../../common/constants';

const processDataForCsvExport = (rows) => {
    const mapDataKeys = (row) => {
        const {
            source, category, businessDate, timestamp, errorMessage, status
        } = row;
        return {
            Source: source,
            Category: category,
            Date: businessDate ? moment(businessDate).format(appConstants.dateFormat) : '',
            Timestamp: timestamp ? moment(timestamp).format(appConstants.dateTimeFormat) : '',
            Message: errorMessage ? errorMessage.replace(/"/g, '') : '',
            Status: status
        };
    };
    return rows.map(mapDataKeys);
};

const calculateCsvFilename = (dateFrom, dateTo) => (
    dateFrom && dateTo ? `WebJobs_${moment(dateFrom).format(appConstants.dateFormat)}_${moment(dateTo).format(appConstants.dateFormat)}.csv`
        : `WebJobs_${moment().format(appConstants.dateFormat)}.csv`
);

const WebJobs = (props) => {
    const {
        webJobsLoaded,
        refreshWebJobsDataLoads,
        loadRequestOpened,
        setAllRowsChecked,
        rows,
        rerunSelectedRows,
        location
    } = props;
    useEffect(() => {
        webJobsLoaded(location.search);
        const continuousDataLoad = setInterval(() => {
            refreshWebJobsDataLoads();
        }, PAGE_REFRESH_RATE);
        return () => clearInterval(continuousDataLoad);
    }, [webJobsLoaded, refreshWebJobsDataLoads, location.search]);

    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback(() => {
        loadRequestOpened();
        setModalOpen(true);
    }, [setModalOpen, loadRequestOpened]);
    const closeModal = useCallback(() => setModalOpen(false), [setModalOpen]);

    const [allRowsChecked, setAllRowsCheckedFunc] = useState(false);

    const setHeaderChecked = useCallback((checked) => {
        setAllRowsChecked(checked);
        setAllRowsCheckedFunc(checked);
    }, [setAllRowsChecked, setAllRowsCheckedFunc]);

    const rerunSelectedRowsFunc = useCallback(() => {
        rerunSelectedRows(rows.filter(row => row.selected).map(row => row.requestId));
    }, [rows, rerunSelectedRows]);

    return (
        <div className={ props.styles.webJobs }>
            <div className={ props.styles.filterHeaderWrapper }>
                <FilterHeader
                    fixed
                    title="Data Load Filters"
                    id="WebJobs"
                />
            </div>
            <div className={ props.styles.gridHeaderContainer }>
                <GridHeader
                    title="Data Loads"
                    styles={ gridHeaderStyles }
                    lastUpdated={ props.lastUpdated }
                    refreshControlsActive
                    refreshData={ props.refreshData }
                >
                    <SimpleGridHeaderButton
                        onClick={ rerunSelectedRowsFunc }
                        text="Re-run selected rows"
                    ><Rerun />
                    </SimpleGridHeaderButton>

                    <div className={ props.styles.separator }>&nbsp;</div>

                    <SimpleGridHeaderButton
                        onClick={ openModal }
                        text="New Load Request"
                    ><AddCircle />
                    </SimpleGridHeaderButton>

                    <div className={ props.styles.separator }>&nbsp;</div>

                    <ExportAsCsvButton
                        csvData={ props.rows }
                        csvFilename={ calculateCsvFilename(props.dateFrom, props.dateTo) }
                        csvMapping={ processDataForCsvExport }
                    />
                </GridHeader>
            </div>
            <div className={ props.styles.gridContainer }>
                <Grid
                    columns={ props.columns }
                    defaultColumnSpacing={ 2 }
                    defaultSorted={ findGridSort(props.defaultSort,
                        'webJobsLoads') }
                    headerChecked={ allRowsChecked }
                    filterable
                    loading={ props.loading }
                    onSortedChange={ (newSorted) => {
                        props.setGridSorts('webJobsLoads', newSorted);
                    } }
                    rerunSelectedRow={ props.rerunSelectedRows }
                    rows={ props.rows }
                    setHeaderChecked={ setHeaderChecked }
                    setRowChecked={ props.setRowChecked }
                    setDetailsBeingViewed={ props.setDetailsBeingViewed }
                    sortable
                />
            </div>
            <Modal
                closeModal={ closeModal }
                isOpen={ modalOpen }
            >
                <CreateLoadRequest
                    createLoadRequest={ props.createLoadRequest }
                    closeModal={ closeModal }
                    loadRequestMappings={ props.loadRequestMappings }
                />
            </Modal>
        </div>
    );
};

WebJobs.propTypes = {
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            visible: PropTypes.bool
        })
    ),
    createLoadRequest: PropTypes.func.isRequired,
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    defaultSort: PropTypes.arrayOf(
        PropTypes.shape({
            gridId: PropTypes.string,
            gridSort: PropTypes.arrayOf(PropTypes.shape({
                id: PropTypes.string,
                desc: PropTypes.bool
            }))
        })
    ),
    lastUpdated: PropTypes.number,
    location: PropTypes.shape({
        search: PropTypes.string
    }).isRequired,
    loading: PropTypes.bool,
    loadRequestOpened: PropTypes.func.isRequired,
    loadRequestMappings: PropTypes.arrayOf(PropTypes.shape({})),
    refreshData: PropTypes.func.isRequired,
    refreshWebJobsDataLoads: PropTypes.func.isRequired,
    rerunSelectedRows: PropTypes.func.isRequired,
    rows: PropTypes.arrayOf(PropTypes.shape({})),
    setAllRowsChecked: PropTypes.func.isRequired,
    setRowChecked: PropTypes.func.isRequired,
    setDetailsBeingViewed: PropTypes.func.isRequired,
    setGridSorts: PropTypes.func.isRequired,
    styles: PropTypes.objectOf(PropTypes.string),
    webJobsLoaded: PropTypes.func
};

WebJobs.defaultProps = {
    columns: [],
    dateFrom: '',
    dateTo: '',
    defaultSort: [],
    lastUpdated: moment.unix(),
    loading: false,
    loadRequestMappings: [],
    rows: [],
    styles: defaultStyles,
    webJobsLoaded: noop
};

const mapStateToProps = state => ({
    columns: combinedColumnsSelector(state),
    dateFrom: getDateFrom(getFilters(state, 'WebJobs')),
    dateTo: getDateTo(getFilters(state, 'WebJobs')),
    defaultSort: getDefaultSort(state),
    lastUpdated: getLastUpdated(state),
    loading: getLoading(state),
    loadRequestMappings: getLoadRequestMappings(state),
    rows: getRows(state)
});

const mapDispatchToProps = {
    createLoadRequest,
    loadRequestOpened,
    refreshData,
    refreshWebJobsDataLoads,
    rerunSelectedRows,
    setAllRowsChecked,
    setRowChecked,
    setDetailsBeingViewed,
    setGridSorts,
    webJobsLoaded
};

export default withRouter(connect(mapStateToProps,
    mapDispatchToProps)(WebJobs));

export { WebJobs as WebJobsUnconnected };
